import { useContext, useMemo } from "react";
import { useHistory, useLocation, useParams } from "react-router";
import { ClipLoader } from "react-spinners";
import { common_utils } from "../../../../../common/common_utils";
import { Constants } from "../../../../../common/constants";
import { SyncEntity } from "../../../../../src/entity/Sync";
import { DashboardUtils } from "../../dashboard_utils";
import { trpc } from "../../utils/trpc";
import { GuildSettingsContext } from "../guild_settings";
import AuthFooter from "./auth_footer";
import { GuildSyncsSection } from "./guild_syncs_section";
import LinkAccount from "./link_account/link_account";
import { GuildSyncsButton } from "./sync_overview/guild_syncs_button";
import SyncOverview from "./sync_overview/sync_overview";
import SyncSetup from "./sync_setup/sync_setup";

enum Mode {
	Overview,
	AdminOverview,
	Create,
	Edit,
}

type State =
	| {
			mode: Mode.Overview | Mode.Create;
	  }
	| { mode: Mode.Edit; editSync: SyncEntity };

// Top level component for sync settings tab
// shows link_account, sync_setup, or sync_overview
// depending on state
export default function SyncSettings() {
	const history = useHistory();
	const URLSearchParams = useURLSearchParams();

	const { server_id } = useParams<{ server_id: string }>();

	const getSyncsOrAuthURLQuery = trpc.gcal.getSyncsOrAuthURL.useQuery({
		guild_id: server_id,
	});

	const { is_admin } = useContext(GuildSettingsContext);

	if (getSyncsOrAuthURLQuery.isLoading) {
		return <>{DashboardUtils.RenderLoadingThrobber()}</>;
	}

	if (!getSyncsOrAuthURLQuery.isSuccess) {
		return (
			<p className="has-text-danger">Error communicating with server</p>
		);
	}

	// get state stored in URL search param, if any
	let modeFromUrl: Mode | null = null;
	let editSyncFromUrl: SyncEntity | undefined;
	{
		const modeFromURLString = URLSearchParams.get("action");
		const editSyncIdString = URLSearchParams.get("editSync");
		const editSyncId =
			editSyncIdString && editSyncIdString.length > 0
				? Number(editSyncIdString)
				: null;

		if (modeFromURLString) {
			const typedModeFromUrlString =
				modeFromURLString as keyof typeof Mode;
			const rawModeFromUrl = Mode[typedModeFromUrlString];

			if (rawModeFromUrl === Mode.Edit && editSyncId !== null) {
				const rawEditSyncFromUrl =
					getSyncsOrAuthURLQuery.data.syncs.find(
						(sync) => sync.id === editSyncId
					);
				if (rawEditSyncFromUrl) {
					modeFromUrl = Mode.Edit;
					editSyncFromUrl = rawEditSyncFromUrl;
				} else {
					// go to overview if we can't find the sync to edit
					modeFromUrl = Mode.Overview;
				}
			} else {
				modeFromUrl = rawModeFromUrl;
			}
		}
	}

	const mode = modeFromUrl ?? Mode.Overview;

	const state = {
		mode,
		editSync: editSyncFromUrl,
	};

	const isLoggedIn = getSyncsOrAuthURLQuery.data.isLoggedIn;

	if (is_admin && state.mode === Mode.AdminOverview) {
		return (
			<div className="flex h-full flex-col gap-2">
				<GuildSyncsSection
					onBackClicked={() =>
						history.push(
							common_utils.getGuildSettingsURL(server_id, "sync")
						)
					}
				/>
				{isLoggedIn && <AuthFooter />}
			</div>
		);
	}

	const noteSection = (
		<div className="note">
			<span className="font-bold">Heads up!</span> This feature is in beta
			and we're still working out a few bugs. See the manual for a{" "}
			<a
				href="/manual#known-issues-and-limitations"
				className="manual-link"
			>
				list of current known issues and limitations
			</a>
			.
		</div>
	);

	if (!isLoggedIn) {
		return (
			<div className="flex flex-col gap-4">
				{noteSection}
				<LinkAccount
					authUrl={getSyncsOrAuthURLQuery.data.authUrl}
					errorMessage={getSyncsOrAuthURLQuery.data.message}
					missingScopes={getSyncsOrAuthURLQuery.data.missingScopes}
				/>
				{is_admin && (
					<GuildSyncsButton
						onClick={() =>
							history.push(
								common_utils.getGuildSettingsURL(
									server_id,
									"sync",
									{
										action: Mode[Mode.AdminOverview],
									}
								)
							)
						}
					/>
				)}
			</div>
		);
	}

	const hasAtLeastOneSync =
		getSyncsOrAuthURLQuery.data.syncs &&
		getSyncsOrAuthURLQuery.data.syncs.length > 0;

	const shouldShowSyncSetup =
		!hasAtLeastOneSync ||
		state.mode === Mode.Create ||
		state.mode === Mode.Edit;

	if (shouldShowSyncSetup) {
		const onBackClicked =
			state.mode === Mode.Create || state.mode === Mode.Edit
				? () => {
						history.push(
							common_utils.getGuildSettingsURL(server_id, "sync")
						);
				  }
				: undefined; // no back button on initial setup page

		return (
			<div className="flex h-full flex-col gap-2">
				<SyncSetup
					onBackClicked={onBackClicked}
					editSync={
						state.mode == Mode.Edit ? state.editSync : undefined
					}
				/>
				<AuthFooter />
			</div>
		);
	}

	return (
		<div className="flex h-full flex-col gap-2">
			{noteSection}
			<SyncOverview
				onSetupNewSyncClicked={() => {
					history.push(
						common_utils.getGuildSettingsURL(server_id, "sync", {
							action: Mode[Mode.Create],
						})
					);
				}}
				onEditClicked={(sync: SyncEntity) => {
					history.push(
						common_utils.getGuildSettingsURL(server_id, "sync", {
							action: Mode[Mode.Edit],
							editSync: sync.id.toString(),
						})
					);
				}}
			/>
			{is_admin && (
				<GuildSyncsButton
					onClick={() =>
						history.push(
							common_utils.getGuildSettingsURL(
								server_id,
								"sync",
								{
									action: Mode[Mode.AdminOverview],
								}
							)
						)
					}
				/>
			)}
			<AuthFooter />
		</div>
	);
}

function useURLSearchParams() {
	const { search } = useLocation();

	return useMemo(() => new URLSearchParams(search), [search]);
}
